import React, { useId, useState } from "react";
import moment from "moment";
import {useTranslation} from "react-i18next";

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Tooltip from '@mui/material/Tooltip';
import { ExpandMore, Info, Send } from '@vedmarket/ui-kit';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import ButtonBase from '@mui/material/ButtonBase';

import {COLORS} from "../../../config/constants";
import Template, { renderSummaryCalculatorInfo } from './components/template';
import { getMainInfo } from "./components/mainInfo";
import { getPackagingInfo } from "./components/packagingInfo";
import { getDeliveryInfo } from "./components/deliveryInfo";
import { getCertificateInfo } from "./components/certificateInfo";
import { getCustomsInfo } from "./components/customsInfo";
import { getBankCommissionsInfo } from "./components/bankCommissionsInfo";
import { getAdditionalInfo } from "./components/additionalInfo";

import './right-panel.scss';
import {numberWithSpaces} from "../../../utils/helpers";

enum CURRENT_STEP {
  MAIN = 'main',
  PACKAGING = 'packaging',
  DELIVERY = 'delivery',
  CUSTOMS = 'customs',
  CERTIFICATE = 'certificate',
  BANK_COMMISSIONS = 'bank_commissions',
  ADDITIONAL = 'additional',
}

const appendInfo = (info: SummaryCalculatorInfoRow[], extra?: SummaryCalculatorInfoRow[]) => (
  extra?.length ? [ ...info, ...extra ].filter((rows) => rows.length) : info
)

const SummaryCalculator = ({
  calculation,
  noInfo,
  noLimeColor,
  demoCurrentStepCompleted,
  demoCurrentStepIndex,
  presentation,
}: SummaryCalculatorProps) => {
  const {
    order,
    summaryCustoms,
    summaryOrder,
    invoices,
    summaryDelivery,
    summaryCompleted,
    summaryCertification,
    declarations,
    extraInfo,
  } = calculation

  const { t } = useTranslation()

  const getInfo = ({ section, info }: SummaryCalculatorTransformInfoArgs) => (
    calculation.transformInfo?.({ section, info }) ?? info
  )

  const shouldShowSubmissionStatus = presentation?.showSubmissionStatus && order?.locked_by_active_application

  const contentId = useId()
  const [ expanded, setExpanded ] = useState(true)

  const [ expandedSteps, setExpandedSteps ] = useState<CURRENT_STEP[]>(() => (
    presentation?.defaultExpandedAll ? Object.values(CURRENT_STEP) : []
  ))

  const handleChangeStep = (step: CURRENT_STEP) => {
    setExpandedSteps((steps) => {
      if (steps.includes(step)) {
        return steps.filter((item) => item !== step)
      }

      return presentation?.defaultExpandedAll ? [ ...steps, step ] : [ step ]
    })
  }

  const getIsStepCompleted = (stepIndex: number, backendValue?: boolean) => {
    if (demoCurrentStepIndex === undefined) {
      return backendValue
    }

    const isPreviousStep = stepIndex < demoCurrentStepIndex
    const isCurrentStepCompleted = stepIndex === demoCurrentStepIndex &&
      Boolean(demoCurrentStepCompleted)

    return isPreviousStep || isCurrentStepCompleted
  }

  const getIsStepDisabled = (stepIndex: number) => (
    demoCurrentStepIndex !== undefined && stepIndex > demoCurrentStepIndex
  )

  const getShouldShowStepInfo = (stepIndex: number) => (
    presentation?.showAllInfo || (demoCurrentStepIndex !== undefined && stepIndex <= demoCurrentStepIndex)
  )

  const isMainCompleted = getIsStepCompleted(0, summaryCompleted?.common)
  const isPackagingCompleted = getIsStepCompleted(1, summaryCompleted?.packaging)
  const isDeliveryCompleted = getIsStepCompleted(2, summaryCompleted?.delivery)
  const isCustomsCompleted = getIsStepCompleted(3, summaryCompleted?.customs)
  const isCertificationCompleted = getIsStepCompleted(4, summaryCompleted?.certification)
  const isBankCommissionsCompleted = getIsStepCompleted(5, summaryCompleted?.banking_commissions)
  const isAdditionalCompleted = getIsStepCompleted(6, summaryCompleted?.additional_services)

  const shouldShowMainInfo = getShouldShowStepInfo(0)
  const shouldShowPackagingInfo = getShouldShowStepInfo(1)
  const shouldShowDeliveryInfo = getShouldShowStepInfo(2)
  const shouldShowCustomsInfo = getShouldShowStepInfo(3)
  const shouldShowCertificationInfo = getShouldShowStepInfo(4)
  const shouldShowBankCommissionsInfo = getShouldShowStepInfo(5)
  const shouldShowAdditionalInfo = getShouldShowStepInfo(6)

  const shouldShowOrderHeader = demoCurrentStepIndex === undefined || isMainCompleted
  const allStepsCompleted = isMainCompleted && isPackagingCompleted && isDeliveryCompleted &&
    isCustomsCompleted && isCertificationCompleted && isBankCommissionsCompleted && isAdditionalCompleted

  const shouldShowTotal = allStepsCompleted || presentation?.showAllInfo
  const totalInfo = getInfo({ section: 'total', info: extraInfo?.total ?? [] })
  const hasCanonicalTotal = summaryOrder?.total_amount != null
  const hasTotal = hasCanonicalTotal || totalInfo.some((rows) => rows.length)

  const showHeaderTotal = presentation?.totalInHeader && (hasCanonicalTotal || calculation.headerTotal != null) && shouldShowTotal
  const canCollapse = Boolean(presentation?.collapsible)
  const hasHeaderActions = showHeaderTotal || canCollapse
  const collapseLabel = expanded ? t('common__text__hideCalculation') : t('common__text__showCalculation')

  const handleCollapse = () => {
    if (presentation?.onCollapse) {
      presentation.onCollapse()
      return
    }

    setExpanded((value) => !value)
  }

  const details = <>
    <Template
      text={`1. ${t('calc__text__step1__name')}`}
      isCompleted={isMainCompleted}
      info={getInfo({ section: 'main', info: appendInfo(getMainInfo(order), extraInfo?.main) })}
      isExpanded={expandedSteps.includes(CURRENT_STEP.MAIN)}
      onClick={() => handleChangeStep(CURRENT_STEP.MAIN)}
      inProgress={Boolean(order?.id)}
      isDisabled={getIsStepDisabled(0)}
      shouldShowInfo={shouldShowMainInfo}
      noLimeColor={noLimeColor}
      hideStatusIcons={presentation?.hideStatusIcons}/>
    <Template
      text={`2. ${t('calc__text__step2__name')}`}
      isCompleted={isPackagingCompleted}
      info={getInfo({ section: 'packaging', info: appendInfo(getPackagingInfo(Boolean(isPackagingCompleted || shouldShowPackagingInfo), invoices, summaryOrder), extraInfo?.packaging) })}
      isExpanded={expandedSteps.includes(CURRENT_STEP.PACKAGING)}
      onClick={() => handleChangeStep(CURRENT_STEP.PACKAGING)}
      isDisabled={getIsStepDisabled(1)}
      shouldShowInfo={shouldShowPackagingInfo}
      noLimeColor={noLimeColor}
      hideStatusIcons={presentation?.hideStatusIcons}
    />
    <Template
      text={`3. ${t('calc__text__step3__name')}`}
      isCompleted={isDeliveryCompleted}
      info={getInfo({ section: 'delivery', info: appendInfo(getDeliveryInfo(summaryDelivery), extraInfo?.delivery) })}
      isExpanded={expandedSteps.includes(CURRENT_STEP.DELIVERY)}
      onClick={() => handleChangeStep(CURRENT_STEP.DELIVERY)}
      isDisabled={getIsStepDisabled(2)}
      shouldShowInfo={shouldShowDeliveryInfo}
      noLimeColor={noLimeColor}
      hideStatusIcons={presentation?.hideStatusIcons}
    />
    <Template
      text={`4. ${t('calc__text__step4__name')}`}
      isCompleted={isCustomsCompleted}
      info={getInfo({ section: 'customs', info: appendInfo(getCustomsInfo(summaryCustoms, declarations), extraInfo?.customs) })}
      isExpanded={expandedSteps.includes(CURRENT_STEP.CUSTOMS)}
      onClick={() => handleChangeStep(CURRENT_STEP.CUSTOMS)}
      isDisabled={getIsStepDisabled(3)}
      shouldShowInfo={shouldShowCustomsInfo}
      noLimeColor={noLimeColor}
      hideStatusIcons={presentation?.hideStatusIcons}
    />
    <Template
      text={`5. ${t('calc__text__step5__name')}`}
      isCompleted={isCertificationCompleted}
      info={getInfo({ section: 'certification', info: appendInfo(getCertificateInfo(summaryCertification), extraInfo?.certification) })}
      isExpanded={expandedSteps.includes(CURRENT_STEP.CERTIFICATE)}
      onClick={() => handleChangeStep(CURRENT_STEP.CERTIFICATE)}
      isDisabled={getIsStepDisabled(4)}
      shouldShowInfo={shouldShowCertificationInfo}
      noLimeColor={noLimeColor}
      hideStatusIcons={presentation?.hideStatusIcons}
    />
    <Template
      text={`6. ${t('calc__text__step6__name')}`}
      isCompleted={isBankCommissionsCompleted}
      info={getInfo({ section: 'bank_commissions', info: getBankCommissionsInfo(order?.bankCommissions, summaryOrder?.total_payment_amount_with_commissions) })}
      isExpanded={expandedSteps.includes(CURRENT_STEP.BANK_COMMISSIONS)}
      onClick={() => handleChangeStep(CURRENT_STEP.BANK_COMMISSIONS)}
      isDisabled={getIsStepDisabled(5)}
      shouldShowInfo={shouldShowBankCommissionsInfo}
      noLimeColor={noLimeColor}
      hideStatusIcons={presentation?.hideStatusIcons}
    />
    <Template
      text={`7. ${t('calc__text__step7__name')}`}
      isCompleted={isAdditionalCompleted}
      info={getInfo({ section: 'additional', info: getAdditionalInfo(order?.userExtraServices) })}
      isExpanded={expandedSteps.includes(CURRENT_STEP.ADDITIONAL)}
      onClick={() => handleChangeStep(CURRENT_STEP.ADDITIONAL)}
      isDisabled={getIsStepDisabled(6)}
      shouldShowInfo={shouldShowAdditionalInfo}
      noLimeColor={noLimeColor}
      hideStatusIcons={presentation?.hideStatusIcons}
    />
    {!noInfo && hasTotal && shouldShowTotal && <>
      <CardContent sx={{
        backgroundColor: noLimeColor ? COLORS.WHITE : COLORS.LIME,
        '&:last-child': {
          padding: '16px 24px',
        },
        ...(calculation.totalHeading ? {
          '& .right-panel-item__details:last-child': { marginBottom: 0 },
        } : {}),
      }}>
        {calculation.totalHeading && <Typography variant='subtitle1' className='right-panel__total-label' sx={{ mb: 2 }}>
          {t('common__text__inTotal').toUpperCase()}
        </Typography>}
        {renderSummaryCalculatorInfo(totalInfo, noLimeColor ? COLORS.WHITE : COLORS.LIME)}
        {!calculation.totalHeading && hasCanonicalTotal && <Typography variant='subtitle1' className={noLimeColor ? 'right-panel__total' : 'right-panel__total right-panel__total_isCompleted'}>
          <span className='right-panel__total-label'>
            <Info style={{
              fill:COLORS.LIGHT_TEXT_DISABLED_OPACITY,
              marginRight: '12px',
            }} />
            {t('common__text__inTotal').toUpperCase()}
          </span>
          <span className='right-panel__total-value'>{`${numberWithSpaces(summaryOrder?.total_amount)} USD`}</span>
        </Typography>}
      </CardContent>
    </>}
  </>

  const header = order?.id ? <>
    <CardContent sx={{ padding: hasHeaderActions ? '16px 24px' : '16px 24px 0' }}>
      <Box className={hasHeaderActions ? 'summary-total__heading' : undefined}>
        <Box className={hasHeaderActions ? 'summary-total__identity' : undefined}>
          <Typography variant='h6' sx={{
            minWidth: 0,
            overflowWrap: 'anywhere',
            wordBreak: 'normal',
          }}>
            {order.order_name}
            {shouldShowSubmissionStatus && <Tooltip placement='right' title={<Typography variant='body2'>
              {t('calc__text__submittedForPreparation')}
            </Typography>}>
              <span
                className='summary-total__submission-status'
                role='img'
                aria-label={t('calc__text__submittedForPreparation')}
                tabIndex={0}
              >
                <Send width={18} height={18} aria-hidden />
              </span>
            </Tooltip>}
          </Typography>
          <Typography variant='caption'>
            {`№ ${order.id} ${t('common__text__from')} ${moment(order.created_at).format('DD.MM.YYYY, HH:mm:ss')}`}
          </Typography>
        </Box>
        {hasHeaderActions && <Box className='summary-total__header-actions'>
          {showHeaderTotal && <Typography component='div' variant='body2' className='summary-total__header-total' sx={{
            color: COLORS.LIGHT_TEXT_SECONDARY,
            whiteSpace: 'nowrap',
          }}>
            {calculation.headerTotal ?? `${numberWithSpaces(summaryOrder?.total_amount)} USD`}
          </Typography>}
          {canCollapse && <Box component='span' className='summary-total__toggle-icon'>
            <ExpandMore aria-hidden style={{
              transform: expanded ? 'rotate(180deg)' : undefined,
              width: 24,
              height: 24,
            }} />
          </Box>}
        </Box>}
      </Box>

    </CardContent>
    <Divider sx={{ marginTop: hasHeaderActions ? 0 : undefined }} />
  </> : null

  return (
    <Card className='summary-total'>
      {!noInfo && header && shouldShowOrderHeader && (canCollapse ? <ButtonBase
        className='summary-total__header-button'
        sx={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
        }}
        aria-label={collapseLabel}
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={handleCollapse}
        focusRipple
      >
        {header}
      </ButtonBase> : header)}
      {canCollapse ? <Collapse in={expanded} id={contentId}>
        {details}
      </Collapse> : details}
    </Card>
  )
}

export default SummaryCalculator
